// Line formatters shared by the prompt-context builders (`reportContext.js`,
// `distributionContext.js`). Extracted rather than duplicated because the two builders read
// the SAME kinds of material in different shapes — a binary reason is `{claim, strength,
// citations}`, a distribution driver is `{text, strength}`, an aggregation entry is a bare
// string — and the rendering ("  - [strong] claim (url)") must stay identical between them,
// or the same forecast reads two different ways to the model depending on its question type.
//
// Pure string work: no React, no store, no I/O.

/** Trim and truncate to `n` chars, appending an ellipsis when cut. */
export function clip(s, n) {
  const t = String(s ?? "").trim();
  return t.length > n ? t.slice(0, n - 1).trimEnd() + "…" : t;
}

/**
 * Up to `max` citation URLs from a report item, as " (url, url)" — or "" when it has none.
 *
 * URLs only, never the citation's `quote`: the quotes are the bulk of a grounded report and
 * the model needs only to know WHICH source backs a claim, not to re-read the passage.
 */
export function citeUrls(item, max) {
  const cites = Array.isArray(item?.citations) ? item.citations : [];
  const urls = cites
    .map((c) => String(c?.source_url ?? c?.url ?? "").trim())
    .filter(Boolean)
    .slice(0, max);
  return urls.length ? ` (${urls.join(", ")})` : "";
}

/**
 * Render report/driver items as "  - claim" lines, one per item.
 *
 * `pick` names which text field to read, so one renderer covers every item shape the report
 * carries (see the module note). `strength` prefixes the report's own weak/moderate/strong
 * rating, so the model can weigh a 12-item list instead of treating it as 12 equals.
 *
 * @param {any} arr items (non-arrays degrade to "")
 * @param {{ max: number, len: number, strength?: boolean, cites?: number,
 *           pick?: (item: any) => any }} opts
 * @returns {string} newline-joined lines, or "" when nothing renders
 */
export function fmtItems(
  arr,
  { max, len, strength = false, cites = 0, pick = (x) => x?.claim ?? x },
) {
  return (Array.isArray(arr) ? arr : [])
    .slice(0, max)
    .map((item) => {
      const text = clip(pick(item), len);
      if (!text) return "";
      const tag = strength && item?.strength ? `[${item.strength}] ` : "";
      return `  - ${tag}${text}${cites ? citeUrls(item, cites) : ""}`;
    })
    .filter(Boolean)
    .join("\n");
}
